/**
 * Date Helper Utilities
 * Centraliza todas as operações com datas para evitar duplicação
 */

import { format, parse, isValid, parseISO, startOfDay, endOfDay, addDays, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Formata uma data para o padrão brasileiro (dd/MM/yyyy)
 */
export const formatDateBR = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    
    return format(dateObj, 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '';
  }
};

/**
 * Formata uma data e hora para o padrão brasileiro (dd/MM/yyyy HH:mm)
 */
export const formatDateTimeBR = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    
    return format(dateObj, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return '';
  }
};

/**
 * Parse uma data no formato brasileiro (dd/MM/yyyy) para Date
 */
export const parseDateBR = (dateString: string): Date | null => {
  if (!dateString || dateString.trim() === '') return null;
  
  try {
    const parsed = parse(dateString, 'dd/MM/yyyy', new Date());
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Valida se uma string está no formato de data brasileiro válido
 */
export const isValidDateBR = (dateString: string): boolean => {
  if (!dateString || dateString.trim() === '') return false;
  
  const parsed = parseDateBR(dateString);
  return parsed !== null;
};

/**
 * Normaliza uma data vinda da API (UTC) para data local sem hora
 */
export const normalizeDateFromApi = (dateValue: string | Date | null | undefined): Date | null => {
  if (!dateValue) return null;
  
  try {
    const originalDate = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!isValid(originalDate)) return null;
    
    return new Date(
      originalDate.getUTCFullYear(),
      originalDate.getUTCMonth(),
      originalDate.getUTCDate()
    );
  } catch {
    return null;
  }
};

/**
 * Converte uma data local para o formato que a API espera (ISO string)
 */
export const formatDateForApi = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;
  
  try {
    const dateObj = typeof date === 'string' ? parseDateBR(date) : date;
    if (!dateObj || !isValid(dateObj)) return null;
    
    return dateObj.toISOString();
  } catch {
    return null;
  }
};

/**
 * Retorna o início do dia (00:00:00.000)
 */
export const getStartOfDay = (date: Date | string = new Date()): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return startOfDay(dateObj);
};

/**
 * Retorna o fim do dia (23:59:59.999)
 */
export const getEndOfDay = (date: Date | string = new Date()): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return endOfDay(dateObj);
};

/**
 * Retorna o primeiro dia do mês
 */
export const getStartOfMonth = (date: Date | string = new Date()): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return startOfMonth(dateObj);
};

/**
 * Retorna o último dia do mês
 */
export const getEndOfMonth = (date: Date | string = new Date()): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return endOfMonth(dateObj);
};

/**
 * Adiciona dias a uma data
 */
export const addDaysToDate = (date: Date | string, days: number): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return addDays(dateObj, days);
};

/**
 * Subtrai dias de uma data
 */
export const subtractDaysFromDate = (date: Date | string, days: number): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return subDays(dateObj, days);
};

/**
 * Calcula a diferença em dias entre duas datas
 */
export const daysBetween = (startDate: Date | string, endDate: Date | string): number => {
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Verifica se uma data está no passado
 */
export const isDateInPast = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const today = getStartOfDay(new Date());
  return dateObj < today;
};

/**
 * Verifica se uma data está no futuro
 */
export const isDateInFuture = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const today = getEndOfDay(new Date());
  return dateObj > today;
};

/**
 * Verifica se uma data é hoje
 */
export const isToday = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const today = new Date();
  
  return (
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear()
  );
};

/**
 * Formata uma data de forma relativa (Hoje, Ontem, Amanhã, ou dd/MM/yyyy)
 */
export const formatDateRelative = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    
    if (isToday(dateObj)) return 'Hoje';
    
    const yesterday = subtractDaysFromDate(new Date(), 1);
    if (isToday(getStartOfDay(yesterday)) && isToday(getStartOfDay(dateObj))) {
      return 'Ontem';
    }
    
    const tomorrow = addDaysToDate(new Date(), 1);
    if (isToday(getStartOfDay(tomorrow)) && isToday(getStartOfDay(dateObj))) {
      return 'Amanhã';
    }
    
    return formatDateBR(dateObj);
  } catch {
    return '';
  }
};

/**
 * Valida intervalo de datas (início deve ser menor ou igual ao fim)
 */
export const isValidDateRange = (startDate: Date | string | null, endDate: Date | string | null): boolean => {
  if (!startDate || !endDate) return true; // Considera válido se algum estiver vazio
  
  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
    
    if (!isValid(start) || !isValid(end)) return false;
    
    return start <= end;
  } catch {
    return false;
  }
};

/**
 * Retorna o nome do mês em português
 */
export const getMonthName = (date: Date | string = new Date()): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMMM', { locale: ptBR });
};

/**
 * Retorna o nome do dia da semana em português
 */
export const getDayOfWeekName = (date: Date | string = new Date()): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'EEEE', { locale: ptBR });
};

